import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom, useAtomValue } from 'jotai';
import { Base_Unit_Type_Enum, Billing_Type_Enum, Contract_Status_Enum } from '@kheops/graphql';
import { FormatPackagingUnitPrice, FormatPackagingVolumePrice, round } from '@kheops/utils';
import { useCustomPriceListByIdLazyQuery } from '../../../queries/__generated__/customPriceListById.generated';
import { currentContextAtom } from '../../../state';
import { DraftCustomPriceList, DraftCustomPriceListPriceCollection } from '../state';
import { usePackagingsByCompanyQuery } from '../../../queries/__generated__/packagingsByCompany.generated';
import { companyPackagingsAtom } from '../../state';

export interface UseCustomPriceListFormDefaultValuesParams {
  customPriceListId?: string;
  copy?: boolean;
  contractId?: string;
}

export default function useCustomPriceListFormDefaultValues(params?: UseCustomPriceListFormDefaultValuesParams): DraftCustomPriceList {
  const { t } = useTranslation(['price']);
  const { companyId } = useAtomValue(currentContextAtom);
  const [customPriceListByIdQuery, { data: customPriceListData }] = useCustomPriceListByIdLazyQuery();
  const { data: customPriceListPackagingsData } = usePackagingsByCompanyQuery({
    variables: {
      companyId,
      skuPackagingsToIgnore: [],
    },
  });
  const [customPriceListPackagings, setCustomPriceListPackagings] = useAtom(companyPackagingsAtom);

  const defaultValues = useMemo((): DraftCustomPriceList => {
    const values: DraftCustomPriceList = {
      name: '',
      contracts: [],
      prices: {},
    };

    if (params?.customPriceListId && !customPriceListData?.custom_price_list_by_pk) {
      return values;
    }

    if (params?.contractId) {
      values.contracts.push(params.contractId);
    }

    if (customPriceListData?.custom_price_list_by_pk) {
      if (params?.copy) {
        values.name = t('price:name_copy', { name: customPriceListData.custom_price_list_by_pk.name });
      } else {
        values.created_at = customPriceListData.custom_price_list_by_pk.created_at;
        values.reference_id = customPriceListData.custom_price_list_by_pk.reference_id;
        values.name = customPriceListData.custom_price_list_by_pk.name;
        values.contracts = customPriceListData.custom_price_list_by_pk.contracts.reduce((acc, { id, status }) => {
          if (status === Contract_Status_Enum.Signed) {
            acc.push(id);
          }

          return acc;
        }, [] as string[]);
      }
    }

    values.prices = customPriceListPackagings.reduce((acc, packaging) => {
      const customPriceListPackaging = customPriceListData?.custom_price_list_by_pk?.custom_price_list_packagings?.find(({ packaging_sku }) => packaging.sku === packaging_sku);
      const discount = customPriceListPackaging?.discount || 0;
      const price = packaging.base_unit.unit_type === Base_Unit_Type_Enum.Bulk || packaging.base_unit.billing_type === Billing_Type_Enum.WeightDependent
        ? FormatPackagingVolumePrice(packaging, packaging.base_unit).value
        : FormatPackagingUnitPrice(packaging);

      acc[packaging.sku] = {
        discount: round(discount * 100),
        price: round(price * (1 - discount), 3),
      };

      return acc;
    }, {} as DraftCustomPriceListPriceCollection);

    return values;
  }, [customPriceListPackagings, customPriceListData]);

  useEffect((): void => {
    if (customPriceListPackagingsData?.packaging?.length) {
      setCustomPriceListPackagings(customPriceListPackagingsData?.packaging);
    }
  }, [customPriceListPackagingsData]);

  useEffect(() => {
    if (params?.customPriceListId) {
      customPriceListByIdQuery({ variables: { id: params.customPriceListId }, fetchPolicy: 'network-only' });
    }
  }, []);

  return defaultValues;
}
